import { useState, useEffect, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Icon } from '../components/Icon'
import { Toast } from '../components/Toast'
import { GeneralTab } from '../components/config/GeneralTab'
import { ReviewRoutingTab } from '../components/config/ReviewRoutingTab'
import { ReviewUpdatesTab } from '../components/config/ReviewUpdatesTab'
import { CrosspostsTab } from '../components/config/CrosspostsTab'
import { NudgesTab } from '../components/config/NudgesTab'
import { DigestTab } from '../components/config/DigestTab'
import { AdvancedTab } from '../components/config/AdvancedTab'
import { FiltersTab } from '../components/config/FiltersTab'
import { getChannel, updateChannel } from '../api/client'
import type { ChannelConfig, ChannelConfigUpdate, RejectedSlug, NotificationType } from '../types'
import styles from './ChannelDetail.module.css'

type Tab = 'general' | 'filters' | 'routing' | 'notifications' | 'crossposts' | 'nudges' | 'digest' | 'advanced'

const ALL_TABS: { id: Tab; label: string; icon: string; adminOnly?: boolean }[] = [
  { id: 'general',       label: 'Subscriptions',    icon: 'person_search' },
  { id: 'filters',       label: 'Filters',           icon: 'filter_alt' },
  { id: 'routing',       label: 'Review Routing',   icon: 'alt_route' },
  { id: 'notifications', label: 'Review Updates',   icon: 'notifications' },
  { id: 'crossposts',    label: 'Crossposts',        icon: 'call_split' },
  { id: 'nudges',        label: 'Nudges',            icon: 'schedule' },
  { id: 'digest',        label: 'Digest',            icon: 'bar_chart' },
  { id: 'advanced',      label: 'Advanced',          icon: 'settings' },
]

const REJECTED_KIND_LABEL: Record<RejectedSlug['kind'], string> = {
  review:        'review team',
  author:        'author team',
  direct_author: 'watched author',
  repo:          'watched repo',
}

const REJECT_REASON: Record<RejectedSlug['kind'], Partial<Record<RejectedSlug['reason'], string>>> = {
  review: {
    not_found: 'Team not found on GitHub',
    org_wide:  'Org-wide teams cannot be used',
    error:     'GitHub unavailable — please retry',
  },
  author: {
    not_found: 'Team not found on GitHub',
    org_wide:  'Org-wide teams cannot be used',
    error:     'GitHub unavailable — please retry',
  },
  direct_author: {
    not_found: 'User not found on GitHub',
    error:     'GitHub unavailable — please retry',
  },
  repo: {
    invalid:   'Not a valid repository',
    not_found: 'Repository not found or not visible to Review Ops',
    archived:  'Repository is archived',
    error:     'GitHub unavailable — please retry',
  },
}

interface Props {
  sessionToken: string
  isAdmin: boolean
}

export function ChannelDetail({ sessionToken, isAdmin }: Props) {
  const { channelId } = useParams<{ channelId: string }>()
  const navigate = useNavigate()

  const [channel, setChannel] = useState<ChannelConfig | null>(null)
  const [draft, setDraft] = useState<ChannelConfigUpdate & {
    githubTeamSlugs: string[]
    authorTeamSlugs: string[]
    mutedGithubUsers: string[]
    watchedAuthorLogins: string[]
    watchedReviewerLogins: string[]
    watchedRepos: string[]
  }>({
    githubTeamSlugs: [],
    authorTeamSlugs: [],
    mutedGithubUsers: [],
    watchedAuthorLogins: [],
    watchedReviewerLogins: [],
    watchedRepos: [],
    scopedUserGroupTagging: true,
    openNotificationTemplate: 'STANDARD',
    reviewReplyTemplate: 'STANDARD',
    mergeStatsEnabled: false,
    enabledNotifications: [],
    reactionNotifications: [],
    replyNotifications: [],
  })
  const [dirty, setDirty] = useState(false)
  const [activeTab, setActiveTab] = useState<Tab>('general')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSilent, setSaveSilent] = useState(false)
  const [rejected, setRejected] = useState<RejectedSlug[]>([])
  const [toast, setToast] = useState<string | null>(null)

  useEffect(() => {
    if (!channelId) return
    setLoading(true)
    getChannel(sessionToken, channelId)
      .then(c => { setChannel(c); resetDraft(c) })
      .catch(() => navigate('/'))
      .finally(() => setLoading(false))
  }, [channelId, sessionToken, navigate])

  function resetDraft(c: ChannelConfig) {
    setDraft({
      prefix:                     c.prefix,
      githubTeamSlugs:            c.githubTeamSlugs ?? [],
      authorTeamSlugs:            c.authorTeamSlugs ?? [],
      paused:                     c.paused,
      enabledNotifications:       c.enabledNotifications,
      nudgeEnabled:               c.nudgeEnabled,
      nudgeThresholdDays:         c.nudgeThresholdDays,
      escalationEnabled:          c.escalationEnabled,
      escalationAfterNudges:      c.escalationAfterNudges,
      weeklyDigestEnabled:        c.weeklyDigestEnabled,
      personalDigestEnabled:      c.personalDigestEnabled,
      timezone:                   c.timezone,
      workingHoursStart:          c.workingHoursStart,
      workingHoursEnd:            c.workingHoursEnd,
      mutedGithubUsers:           c.mutedGithubUsers ?? [],
      watchedAuthorLogins:        c.watchedAuthorLogins ?? [],
      watchedReviewerLogins:      c.watchedReviewerLogins ?? [],
      watchedRepos:               c.watchedRepos ?? [],
      reactionNotifications:      c.reactionNotifications,
      replyNotifications:         c.replyNotifications,
      repoFilterMode:               c.repoFilterMode,
      filteredRepos:                c.filteredRepos ?? [],
      labelFilterMode:              c.labelFilterMode,
      filteredLabels:               c.filteredLabels ?? [],
      sizeFilterMode:               c.sizeFilterMode,
      filteredSizes:                c.filteredSizes ?? [],
      authorFilterMode:             c.authorFilterMode,
      filteredAuthors:              c.filteredAuthors ?? [],
      scopedUserGroupTagging:       c.scopedUserGroupTagging,
      reviewerTaggingEnabled:       c.reviewerTaggingEnabled,
      dmReviewersEnabled:           c.dmReviewersEnabled,
      crosspostEnabled:             c.crosspostEnabled,
      receiveCrosspostsEnabled:        c.receiveCrosspostsEnabled,
      crosspostReactionNotifications:  c.crosspostReactionNotifications,
      crosspostReplyNotifications:     c.crosspostReplyNotifications,
      crosspostReviewerTaggingEnabled: c.crosspostReviewerTaggingEnabled,
      crosspostMessageTemplate:        c.crosspostMessageTemplate,
      openNotificationTemplate:     c.openNotificationTemplate,
      reviewReplyTemplate:          c.reviewReplyTemplate,
      mergeStatsEnabled:            c.mergeStatsEnabled,
      configuredAt:                 c.configuredAt,
    })
    setDirty(false)
    setSaveError(null)
    setRejected([])
  }

  const patch = useCallback((update: Partial<ChannelConfigUpdate>) => {
    setDraft(d => ({ ...d, ...update }))
    setDirty(true)
    setSaveError(null)
    setRejected([])
  }, [])

  async function save() {
    if (!channelId) return
    setSaving(true)
    setSaveError(null)
    setRejected([])
    try {
      const payload = isAdmin && saveSilent ? { ...draft, silent: true } : draft
      const { config, rejected: rej } = await updateChannel(sessionToken, channelId, payload)
      setChannel(config)
      resetDraft(config)

      if (rej.length > 0) {
        setRejected(rej)
        const retryable = rej.some(r => r.reason === 'error')
        setSaveError(
          retryable
            ? `${rej.length} ${rej.length > 1 ? 'entries' : 'entry'} could not be validated — GitHub may be unavailable. Try again.`
            : `${rej.length} ${rej.length > 1 ? 'entries' : 'entry'} were invalid and not saved.`
        )
      } else {
        setToast('Changes saved successfully')
      }
    } catch (err: unknown) {
      const apiErr = err as { status?: number; message?: string; field?: string }
      if (apiErr?.status === 409) {
        setSaveError('Someone else saved changes. Refresh and try again.')
      } else if (apiErr?.field) {
        setSaveError(`${apiErr.field}: ${apiErr.message}`)
      } else {
        setSaveError(apiErr?.message ?? 'Failed to save. Please try again.')
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState}>Loading…</div>
      </div>
    )
  }

  if (!channel) return null

  return (
    <div className={`${styles.page} animate-slide-in-right`}>
      {/* Sticky sub-topbar */}
      <div className={styles.topbar}>
        <button className={styles.backBtn} onClick={() => navigate('/')}>
          <Icon name="arrow_back" size="sm" /> Channels
        </button>
        <div className={styles.topbarDivider} />
        <span className={styles.topbarName}>{channel.channelName}</span>
        <span className={`badge ${channel.paused ? 'badge-yellow' : 'badge-green'}`} style={{ marginLeft: 8 }}>
          <span className="badge-dot" />
          {channel.paused ? 'Paused' : 'Active'}
        </span>
        {(dirty || saveError) && (
          <div className={styles.topbarActions}>
            {saveError && (
              <span className={styles.topbarError}>
                <Icon name="error_outline" size="sm" /> {saveError}
              </span>
            )}
            {dirty && (
              <>
                <button className={styles.discardBtn} onClick={() => resetDraft(channel)} disabled={saving}>
                  Discard
                </button>
                {isAdmin && (
                  <div className={styles.silentToggle}>
                    <button
                      className={`${styles.silentBtn} ${!saveSilent ? styles.silentBtnActive : ''}`}
                      onClick={() => setSaveSilent(false)}
                      title="Post audit message on save"
                    >
                      <Icon name="volume_up" size="sm" /> Notify
                    </button>
                    <button
                      className={`${styles.silentBtn} ${saveSilent ? styles.silentBtnActive : ''}`}
                      onClick={() => setSaveSilent(true)}
                      title="Save without posting audit message"
                    >
                      <Icon name="volume_off" size="sm" /> Silent
                    </button>
                  </div>
                )}
                <button className={styles.saveBtn} onClick={save} disabled={saving}>
                  {saving
                    ? <><Icon name="hourglass_empty" size="sm" /> Saving…</>
                    : <><Icon name="check" size="sm" /> Save changes</>
                  }
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Rejected slugs banner */}
      {rejected.length > 0 && (
        <div className={styles.rejectedBanner}>
          <Icon name="warning_amber" size="sm" style={{ color: 'var(--yellow)', flexShrink: 0 }} />
          <div>
            {rejected.map(r => (
              <div key={`${r.kind}-${r.slug}`} className={styles.rejectedItem}>
                <code>{r.slug}</code>
                <span className={styles.rejectedKind}>{REJECTED_KIND_LABEL[r.kind]}</span>
                <span className={styles.rejectedReason}>{REJECT_REASON[r.kind]?.[r.reason] ?? r.reason}</span>
                {r.reason === 'error' && (
                  <button className={styles.retryBtn} onClick={save}>Retry</button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Config card */}
      <div className={styles.body}>
        <div className={styles.card}>
          <div className={styles.tabs}>
            {ALL_TABS.filter(t => !t.adminOnly || isAdmin).map(t => (
              <button
                key={t.id}
                className={`${styles.tab} ${activeTab === t.id ? styles.tabActive : ''}`}
                onClick={() => setActiveTab(t.id)}
              >
                <Icon name={t.icon} size="sm" />
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === 'general' && (
            <GeneralTab
              draft={draft as Parameters<typeof GeneralTab>[0]['draft']}
              authorTeamMemberLogins={channel?.authorTeamMemberLogins}
              onChange={patch}
            />
          )}
          {activeTab === 'filters' && (
            <FiltersTab draft={draft} onChange={patch} />
          )}
          {activeTab === 'routing' && (
            <ReviewRoutingTab
              channelId={channelId!}
              sessionToken={sessionToken}
              isAdmin={isAdmin}
              draft={draft}
              onChange={patch}
            />
          )}
          {activeTab === 'notifications' && (
            <ReviewUpdatesTab
              draft={{
                enabledNotifications:        (draft.enabledNotifications   as NotificationType[] | undefined) ?? [],
                reactionNotifications:       (draft.reactionNotifications  as NotificationType[] | undefined) ?? [],
                replyNotifications:          (draft.replyNotifications     as NotificationType[] | undefined) ?? [],
                openNotificationTemplate:    draft.openNotificationTemplate,
                reviewReplyTemplate:         draft.reviewReplyTemplate,
                mergeStatsEnabled:           draft.mergeStatsEnabled,
              }}
              onChange={patch}
            />
          )}
          {activeTab === 'crossposts' && (
            <CrosspostsTab
              isAdmin={isAdmin}
              draft={{
                receiveCrosspostsEnabled:        draft.receiveCrosspostsEnabled,
                crosspostReactionNotifications:  draft.crosspostReactionNotifications as NotificationType[] | undefined,
                crosspostReplyNotifications:     draft.crosspostReplyNotifications as NotificationType[] | undefined,
                crosspostReviewerTaggingEnabled: draft.crosspostReviewerTaggingEnabled,
                crosspostMessageTemplate:        draft.crosspostMessageTemplate,
                enabledNotifications:            draft.enabledNotifications as NotificationType[] | undefined,
                reactionNotifications:           draft.reactionNotifications as NotificationType[] | undefined,
                replyNotifications:              draft.replyNotifications as NotificationType[] | undefined,
                reviewerTaggingEnabled:          draft.reviewerTaggingEnabled,
              }}
              onChange={patch}
            />
          )}
          {activeTab === 'nudges' && (
            <NudgesTab draft={draft as Parameters<typeof NudgesTab>[0]['draft']} onChange={patch} />
          )}
          {activeTab === 'digest' && (
            <DigestTab draft={draft as Parameters<typeof DigestTab>[0]['draft']} onChange={patch} />
          )}
          {activeTab === 'advanced' && (
            <AdvancedTab
              channelId={channelId!}
              sessionToken={sessionToken}
              isAdmin={isAdmin}
              draft={draft as Parameters<typeof AdvancedTab>[0]['draft']}
              onChange={patch}
              onDeleted={() => navigate('/')}
            />
          )}
        </div>
      </div>

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  )
}
